import { Directive, OnInit, OnDestroy, NgZone, Renderer2 } from "@angular/core";

/**
 * Manages sidebar expand/collapse, split-search expand/collapse,
 * drag-to-resize (horizontal + vertical), and file-upload wiring.
 *
 * Apply once at the app-root level:
 *   <app-root wgPanelManager>
 */
@Directive({
  selector: "[wgPanelManager]",
  standalone: false,
})
export class WgPanelManagerDirective implements OnInit, OnDestroy {
  private readonly COLLAPSED_W = "320px";
  private readonly MIN_SIDEBAR = 200;
  private readonly MAX_SIDEBAR_RATIO = 0.7;
  private readonly MIN_SPLIT_H = 150;

  private observer: MutationObserver | null = null;
  private intervalId: any = null;

  // DOM references
  private sidebarToggleBtn: HTMLElement | null = null;
  private sidebarDragHandle: HTMLElement | null = null;
  private splitToggleBtn: HTMLElement | null = null;
  private splitDragHandle: HTMLElement | null = null;

  // Cleanup listeners
  private cleanupFns: (() => void)[] = [];

  constructor(
    private zone: NgZone,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    // Run outside Angular zone — pure DOM manipulation, no change detection needed
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.onDomChange());
      this.observer.observe(document.body, { childList: true, subtree: true });

      // Also poll at low frequency as a fallback (dynamic component rendering)
      this.intervalId = setInterval(() => this.onDomChange(), 1500);
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.intervalId) clearInterval(this.intervalId);
    this.cleanup();
  }

  // ─── Main orchestrator ───────────────────────────────────────────

  private onDomChange(): void {
    const sidebar = document.querySelector<HTMLElement>(".side-input-form");
    const splitForm = document.querySelector<HTMLElement>(".split-s-form");

    this.manageSidebarToggle(sidebar, splitForm);
    this.manageSidebarDrag(sidebar);
    this.manageSplitToggle(splitForm);
    this.manageSplitDrag(splitForm);
    this.wireFileUploads(sidebar);
    // The plain in-flow panel (see the .new-main-input-form +
    // .right-side-input-form block at the end of tailwind.css). It
    // deliberately gets no toggle button and no drag handle, but its file
    // uploads still need wiring.
    this.wireFileUploads(
      document.querySelector<HTMLElement>(".right-side-input-form"),
    );
    this.manageDynamicSidebar();

    // Clean up CSS variable when sidebar disappears
    if (!sidebar) {
      document.body.classList.remove("wg-sidebar-dragged");
      document.documentElement.style.removeProperty("--wg-sidebar-w");
    }
  }

  // ─── 6. Dynamic form sidebar (.dynamic-sidebar-container) ───────
  private manageDynamicSidebar(): void {
    const sidebars = document.querySelectorAll<HTMLElement>(
      ".dynamic-sidebar-container",
    );
    sidebars.forEach((el) => {
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
    });

    document.body.classList.toggle(
      "wg-has-dynamic-sidebar",
      sidebars.length > 0,
    );
  }

  // ─── 1. Sidebar expand/collapse toggle ───────────────────────────

  private manageSidebarToggle(
    sidebar: HTMLElement | null,
    splitForm: HTMLElement | null,
  ): void {
    if (sidebar && !this.sidebarToggleBtn) {
      this.sidebarToggleBtn = this.createSidebarToggle();
      document.body.appendChild(this.sidebarToggleBtn);
    }
    if (!sidebar && this.sidebarToggleBtn) {
      this.sidebarToggleBtn.remove();
      this.sidebarToggleBtn = null;
      document.body.classList.remove("wg-sidebar-expanded");
    }

    // Adjust vertical position based on split-screen visibility
    if (this.sidebarToggleBtn) {
      if (splitForm) {
        const splitExp = document.body.classList.contains("wg-split-expanded");
        this.sidebarToggleBtn.style.top = splitExp ? "10%" : "25%";
      } else {
        this.sidebarToggleBtn.style.top = "50%";
      }
    }
  }

  private createSidebarToggle(): HTMLElement {
    const btn = document.createElement("button");
    btn.id = "wg-sidebar-toggle";
    btn.title = "Razširi / Skrči panel";
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>`;

    Object.assign(btn.style, {
      position: "fixed",
      right: this.COLLAPSED_W,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: "25",
      width: "14px",
      height: "40px",
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRight: "none",
      borderRadius: "9999px 0 0 9999px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxShadow: "-2px 0 4px rgba(0,0,0,.04)",
      padding: "0",
      transition: "right .3s ease, background .15s, box-shadow .15s",
      outline: "none",
    });

    const svg = btn.querySelector("svg");
    if (svg) {
      (svg as any).style.transition = "transform .3s ease";
      (svg as any).style.color = "#94a3b8";
    }

    btn.onmouseenter = () => {
      btn.style.background = "#f8fafc";
      btn.style.boxShadow = "-2px 0 8px rgba(0,0,0,.08)";
      if (svg) (svg as any).style.color = "#475569";
    };
    btn.onmouseleave = () => {
      btn.style.background = "#fff";
      btn.style.boxShadow = "-2px 0 4px rgba(0,0,0,.04)";
      if (svg) (svg as any).style.color = "#94a3b8";
    };

    btn.onclick = () => {
      // Clear any drag-set inline overrides so CSS class rules take effect
      const sb = document.querySelector<HTMLElement>(".side-input-form");
      const sc = document.querySelector<HTMLElement>(".wg-scroll-container");
      const ic = document.querySelector<HTMLElement>(
        ".sub-content .item-container:not(.wg-search)",
      );
      const sf = document.querySelector<HTMLElement>(".split-s-form");

      for (const el of [sb]) {
        if (el) {
          el.style.removeProperty("width");
          el.style.removeProperty("min-width");
          el.style.removeProperty("max-width");
        }
      }
      // Width is now CSS-variable driven, no inline margin-right to clear
      if (sf) sf.style.removeProperty("right");

      document.body.classList.remove("wg-sidebar-dragged");

      const expanded = document.body.classList.toggle("wg-sidebar-expanded");
      // Drive content width via CSS variable
      document.documentElement.style.setProperty(
        "--wg-sidebar-w",
        expanded ? "50vw" : "320px",
      );
      btn.style.right = expanded ? "50%" : this.COLLAPSED_W;
      if (svg) (svg as any).style.transform = expanded ? "rotate(180deg)" : "";
    };

    return btn;
  }

  // ─── 2. Sidebar drag-to-resize (horizontal) ─────────────────────

  private manageSidebarDrag(sidebar: HTMLElement | null): void {
    if (sidebar && !this.sidebarDragHandle) {
      this.sidebarDragHandle = this.createSidebarDrag(sidebar);
      document.body.appendChild(this.sidebarDragHandle);
    }
    // Keep handle aligned with sidebar width
    if (
      sidebar &&
      this.sidebarDragHandle &&
      !(this.sidebarDragHandle as any)._dragging
    ) {
      const w = sidebar.getBoundingClientRect().width || 320;
      this.sidebarDragHandle.style.right = w - 2 + "px";
    }
    if (!sidebar && this.sidebarDragHandle) {
      this.sidebarDragHandle.remove();
      this.sidebarDragHandle = null;
    }
  }

  private createSidebarDrag(sidebar: HTMLElement): HTMLElement {
    const handle = document.createElement("div");
    handle.id = "wg-sidebar-drag";
    const sW = sidebar.getBoundingClientRect().width || 320;

    Object.assign(handle.style, {
      position: "fixed",
      top: "100px",
      bottom: "0",
      right: sW - 2 + "px",
      width: "5px",
      zIndex: "26",
      cursor: "col-resize",
      background: "transparent",
    });

    handle.onmouseenter = () => {
      handle.style.background = "rgba(59,130,246,.18)";
    };
    handle.onmouseleave = () => {
      if (!(handle as any)._dragging) handle.style.background = "transparent";
    };

    handle.onmousedown = (e: MouseEvent) => {
      e.preventDefault();
      (handle as any)._dragging = true;
      handle.style.background = "rgba(59,130,246,.25)";
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";

      const els = {
        sb: document.querySelector<HTMLElement>(".side-input-form"),
        sc: document.querySelector<HTMLElement>(".wg-scroll-container"),
        ic: document.querySelector<HTMLElement>(
          ".sub-content .item-container:not(.wg-search)",
        ),
        sf: document.querySelector<HTMLElement>(".split-s-form"),
        tb: this.sidebarToggleBtn,
      };

      // Disable transitions during drag
      for (const el of [els.sb, els.sc, els.ic, els.sf]) {
        if (el) el.style.setProperty("transition", "none", "important");
      }
      const tbOldTransition = els.tb?.style.transition || "";
      if (els.tb) els.tb.style.transition = "none";

      const onMove = (ev: MouseEvent) => {
        let w = window.innerWidth - ev.clientX;
        w = Math.max(
          this.MIN_SIDEBAR,
          Math.min(w, Math.floor(window.innerWidth * this.MAX_SIDEBAR_RATIO)),
        );
        const px = w + "px";

        if (els.sb) {
          els.sb.style.setProperty("width", px, "important");
          els.sb.style.setProperty("min-width", px, "important");
          els.sb.style.setProperty("max-width", px, "important");
        }
        // Content width driven by --wg-sidebar-w CSS variable on item-container
        // No need to set margin-right on scroll container or item-container
        if (els.sf) els.sf.style.setProperty("right", px, "important");
        if (els.tb) els.tb.style.right = px;
        handle.style.right = w - 2 + "px";

        document.documentElement.style.setProperty("--wg-sidebar-w", px);
        document.body.classList.add("wg-sidebar-dragged");
      };

      const onUp = () => {
        (handle as any)._dragging = false;
        handle.style.background = "transparent";
        document.body.style.userSelect = "";
        document.body.style.cursor = "";

        for (const el of [els.sb, els.sc, els.ic, els.sf]) {
          if (el) el.style.removeProperty("transition");
        }
        if (els.tb) els.tb.style.transition = tbOldTransition;

        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    };

    return handle;
  }

  // ─── 3. Split-search expand/collapse toggle ──────────────────────

  private manageSplitToggle(splitForm: HTMLElement | null): void {
    if (splitForm && !this.splitToggleBtn) {
      this.splitToggleBtn = this.createSplitToggle();
      splitForm.appendChild(this.splitToggleBtn);
    }
    if (!splitForm && this.splitToggleBtn) {
      this.splitToggleBtn.remove();
      this.splitToggleBtn = null;
      document.body.classList.remove("wg-split-expanded");
    }
  }

  private createSplitToggle(): HTMLElement {
    const btn = document.createElement("button");
    btn.id = "wg-split-toggle";
    btn.title = "Razširi / Skrči iskanje";
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M18 15.75 12 8.25l-6 7.5"/></svg>`;

    Object.assign(btn.style, {
      position: "absolute",
      top: "0",
      left: "50%",
      transform: "translate(-50%,-100%)",
      zIndex: "25",
      width: "40px",
      height: "14px",
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderBottom: "none",
      borderRadius: "9999px 9999px 0 0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxShadow: "0 -2px 4px rgba(0,0,0,.04)",
      padding: "0",
      transition: "background .15s, box-shadow .15s",
      outline: "none",
    });

    const svg = btn.querySelector("svg");
    if (svg) {
      (svg as any).style.transition = "transform .3s ease";
      (svg as any).style.color = "#94a3b8";
    }

    btn.onmouseenter = () => {
      btn.style.background = "#f8fafc";
      btn.style.boxShadow = "0 -2px 8px rgba(0,0,0,.08)";
      if (svg) (svg as any).style.color = "#475569";
    };
    btn.onmouseleave = () => {
      btn.style.background = "#fff";
      btn.style.boxShadow = "0 -2px 4px rgba(0,0,0,.04)";
      if (svg) (svg as any).style.color = "#94a3b8";
    };

    btn.onclick = () => {
      const expanded = document.body.classList.toggle("wg-split-expanded");
      if (svg) (svg as any).style.transform = expanded ? "rotate(180deg)" : "";
    };

    return btn;
  }

  // ─── 4. Split-search drag-to-resize (vertical) ──────────────────

  private manageSplitDrag(splitForm: HTMLElement | null): void {
    if (splitForm && !this.splitDragHandle) {
      this.splitDragHandle = this.createSplitDrag();
      splitForm.appendChild(this.splitDragHandle);
    }
    if (!splitForm && this.splitDragHandle) {
      this.splitDragHandle.remove();
      this.splitDragHandle = null;
    }
  }

  private createSplitDrag(): HTMLElement {
    const handle = document.createElement("div");
    handle.id = "wg-split-drag";

    Object.assign(handle.style, {
      position: "absolute",
      top: "-3px",
      left: "0",
      right: "0",
      height: "6px",
      zIndex: "26",
      cursor: "row-resize",
      background: "transparent",
    });

    handle.onmouseenter = () => {
      handle.style.background = "rgba(59,130,246,.18)";
    };
    handle.onmouseleave = () => {
      if (!(handle as any)._dragging) handle.style.background = "transparent";
    };

    handle.onmousedown = (e: MouseEvent) => {
      e.preventDefault();
      (handle as any)._dragging = true;
      handle.style.background = "rgba(59,130,246,.25)";
      document.body.style.userSelect = "none";
      document.body.style.cursor = "row-resize";

      const sf = document.querySelector<HTMLElement>(".split-s-form");
      const sv = document.querySelector<HTMLElement>(".splitted-view");
      if (sf) sf.style.setProperty("transition", "none", "important");

      const onMove = (ev: MouseEvent) => {
        let h = window.innerHeight - ev.clientY;
        h = Math.max(this.MIN_SPLIT_H, Math.min(h, window.innerHeight - 100));
        if (sf) sf.style.setProperty("height", h + "px", "important");
        if (sv) sv.style.setProperty("padding-bottom", h + "px", "important");
      };

      const onUp = () => {
        (handle as any)._dragging = false;
        handle.style.background = "transparent";
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        if (sf) sf.style.removeProperty("transition");
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    };

    return handle;
  }

  // ─── 5. File upload wiring ("Dodaj" + image click) ───────────────

  private wireFileUploads(sidebar: HTMLElement | null): void {
    if (!sidebar) return;

    // "Dodaj" → file picker via component's onFileChange
    const opts = sidebar.querySelectorAll<HTMLElement>(
      ".img-options:not([data-wg-wired])",
    );
    opts.forEach((el) => {
      el.setAttribute("data-wg-wired", "1");
      el.addEventListener("click", (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.tagName === "INPUT") return;
        if (target.closest?.(".search")) return;

        const fu = el.closest("sem-file-upload");
        const comp =
          fu &&
          typeof (window as any).ng !== "undefined" &&
          (window as any).ng.getComponent
            ? (window as any).ng.getComponent(fu)
            : null;

        if (comp?.onFileChange) {
          const tmp = document.createElement("input");
          tmp.type = "file";
          tmp.multiple = true;
          tmp.style.display = "none";

          tmp.addEventListener("change", (evt: Event) => {
            const fi = comp.fileUploadService;
            const shared = fi && fi.filesInfo === comp.files;
            if (shared) fi.filesInfo = comp.files.slice();
            comp.onFileChange(evt);
            if (shared) fi.filesInfo = comp.files;
            if (comp.changeDetection) comp.changeDetection.detectChanges();
            document.body.removeChild(tmp);
          });

          document.body.appendChild(tmp);
          tmp.click();
        }
      });
    });

    // Image click → openFileExplorer dialog
    const imgs = sidebar.querySelectorAll<HTMLElement>(
      ".afu-dragndrop-box:not(.upload-img):not([data-wg-img-wired])",
    );
    imgs.forEach((imgEl) => {
      imgEl.setAttribute("data-wg-img-wired", "1");
      imgEl.style.cursor = "pointer";
      imgEl.addEventListener("click", () => {
        const fu = imgEl.closest("sem-file-upload");
        if (fu) {
          const s = fu.querySelector<HTMLElement>(".search");
          if (s) s.click();
        }
      });
    });
  }

  private cleanup(): void {
    this.sidebarToggleBtn?.remove();
    this.sidebarDragHandle?.remove();
    this.splitToggleBtn?.remove();
    this.splitDragHandle?.remove();
    this.sidebarToggleBtn = null;
    this.sidebarDragHandle = null;
    this.splitToggleBtn = null;
    this.splitDragHandle = null;
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];
  }
}
